import { useNavigate } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import { Box, styled } from '@mui/material'
import { TableChartOutlined } from '@mui/icons-material'
import TilesetsIcon from 'assets/icons/carto-icon-tilesets.svg?react'
import RastersIcon from 'assets/icons/carto-icon-rasters.svg?react'
import { EllipsisWithTooltip, Typography } from '@carto/meridian-ds/components'
import { ICON_SIZE_MEDIUM } from '@carto/meridian-ds/theme'
import { Connection, Dataset } from 'features/connections/types'
import CardBase from 'features/common/ui/Cards/CardBase/CardBase'
import { getNodePath } from 'features/data-explorer/utils'
import { useAppDispatch, useAppSelector } from 'app/state'
import { getConnectionByName, setDefaultConnection } from 'features/connections'
import { useGlobalDataExplorerTreeState } from 'features/data-explorer/ui/DataTree/dataTreeCommons'
import { useBookmarkItem } from 'features/bookmarks/hooks/useBookmarks'
import BookmarkButton from 'features/bookmarks/ui/BookmarkButton'

const CardRoot = styled(CardBase)(({ theme }) => ({
  padding: theme.spacing(2)
}))

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  marginRight: theme.spacing(1),

  '& svg': {
    width: ICON_SIZE_MEDIUM,
    height: ICON_SIZE_MEDIUM,

    '& path': {
      fill: theme.palette.primary.dark
    }
  }
}))

const Type = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.dark,
  textTransform: 'capitalize',

  '&:not(:last-child)::after': {
    content: '"·"',
    margin: theme.spacing(0, 0.5),
    color: theme.palette.text.primary
  }
}))

const IconByType = {
  table: <TableChartOutlined />,
  tileset: <TilesetsIcon />,
  raster: <RastersIcon />
}

export function HomeDatasetsCard({ name, connectionName, connectionProviderId, type, fqn }: Dataset) {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { setSelectedTreeItemId } = useGlobalDataExplorerTreeState()
  const connection = useAppSelector((state) => getConnectionByName(state, { connection: connectionName }))
  const { starred } = useBookmarkItem({
    connection: connection as Connection,
    fqn
  })

  const onClick = () => {
    setSelectedTreeItemId(fqn)
    dispatch(setDefaultConnection(connection))
    navigate(getNodePath({ id: fqn, connectionId: connectionName, type }))
  }

  return (
    <CardRoot onClick={onClick}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconWrapper>{IconByType[type]}</IconWrapper>
          <Type variant="caption" weight="strong">
            {type}
          </Type>
          <Typography variant="caption" weight="strong">
            <FormattedMessage id={`data-explorer.providers.${connectionProviderId}`} />
          </Typography>
        </Box>
        {starred && <BookmarkButton starred={starred} size="small" backdrop="none" />}
      </Box>
      <EllipsisWithTooltip>
        <Typography variant="body2" weight="strong">
          {name}
        </Typography>
      </EllipsisWithTooltip>
    </CardRoot>
  )
}

export default HomeDatasetsCard
